"use client";

/**
 * Supabase Browser Client
 *
 * Creates a Supabase client for browser-side usage.
 * Uses Supabase Auth directly (no longer integrated with Clerk).
 *
 * Migration Note: This file previously used Clerk session tokens.
 * Now uses native Supabase Auth session management.
 */
import { createBrowserClient } from "@supabase/ssr";

import { supabaseConfig } from "@/lib/config/env";
import type { Database } from "@/types/supabase";

/**
 * Get a Supabase browser client
 * Use this in client components for database access
 */
export function getSupabaseBrowserClient() {
  const url = supabaseConfig.url;
  const anonKey = supabaseConfig.anonKey;

  if (!url || !anonKey) {
    console.warn("[Supabase] URL or anon key not configured");
    return null;
  }

  return createBrowserClient<Database>(url, anonKey);
}

/**
 * Hook that returns a Supabase browser client
 * Named as a hook for consistent usage patterns
 */
export function useSupabaseClient() {
  return getSupabaseBrowserClient();
}

/**
 * Legacy export for backwards compatibility
 * @deprecated Use useSupabaseClient or getSupabaseBrowserClient instead
 */
export function useClerkSupabaseClient() {
  console.warn("[Supabase] useClerkSupabaseClient is deprecated. Use useSupabaseClient instead.");
  return getSupabaseBrowserClient();
}

/**
 * Legacy export for backwards compatibility
 * @deprecated Use getSupabaseBrowserClient instead
 */
export const createClerkSupabaseClient = getSupabaseBrowserClient;
